import { useEffect, useRef, useState } from 'react';
import { useReduxState } from 'hooks';
import axios from 'axios';
import JSZip from 'jszip';
import { parseString } from 'xml2js';
import { getMessageWithPlaceholders } from 'shared-locales';
import { getBlockFileForLanguage } from 'lib/courses';
import { TaskPointWeightSelector } from 'components/common/tasks';
import { FileUploader } from 'components/common/uploader';
import {
  defaultBlockData,
  EditorBlockTranslationReplace
} from 'components/editor/blocks';
import {
  EditorBlockEmbedPreview,
  EditorBlockEmbedFileControls
} from 'components/editor/blocks/embed';
import {
  mapScormResources,
  mapScormObjectList
} from './EditorBlockScormHandler';

import type { BlockScorm } from 'shared-types';
import type { BlockProps } from 'components/editor/blocks/EditorBlock';
import type { BlockEmbedStateProps } from '../embed/EditorBlockEmbed';

const MAX_UPLOAD_FILE_POLLING_TRIES = 10;

interface FileType {
  name: string;
  type: string;
  data: any;
}

interface FileUrls {
  fileUrl: string;
  latestUploadUrl: string;
  manifestUploadUrl: string;
}

/* We usually don't log to the console, but as an exception this component uses console.error
statements to help with troubleshooting problems. These logs should not be removed. */
const EditorBlockScorm = ({
  blockId,
  data,
  isPreview,
  languageVersion,
  updateData,
  ...stateProps
}: BlockProps<BlockScorm> & BlockEmbedStateProps) => {
  const { editorLanguageActive } = stateProps;

  const {
    messages,
    theme,
    pageState: { course }
  } = useReduxState();

  const [errorCount, setErrorCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const [showLanguageSpecificUploader, setShowLanguageSpecificUploader] =
    useState(false);

  const scormUploadRefreshTimeout = useRef<number | null>(null);
  const scormUploadCompleteTimeout = useRef<number | null>(null);

  // clear timers on component unmount
  useEffect(() => {
    return () => {
      if (scormUploadRefreshTimeout.current) {
        clearTimeout(scormUploadRefreshTimeout.current);
      }
      if (scormUploadCompleteTimeout.current) {
        clearTimeout(scormUploadCompleteTimeout.current);
      }
    };
  }, []);

  const resetError = () => {
    setErrorCount(0);
  };

  const changePointsWeight = (value: number) => {
    if (isEditingLanguageVersion) return;
    updateData({ ...data, pointsWeight: value });
  };

  const handleIsTask = (value: boolean) => {
    updateData({
      ...data,
      isTask: value,
      ...(value ? {} : { isMandatory: false, requireAnswerToProceed: false })
    });
  };

  const handleScormError = (errorMessage?: string) => {
    const errorMsg =
      errorMessage || messages.editorBlockEmbedScormFileNotSupported;
    return { fileError: errorMsg };
  };

  const handleScormParseImsManifest = async (
    file: FileType,
    zip: JSZip,
    path: string,
    latestFile: string
  ): Promise<any> => {
    // found imsmanifest.xml, read file content and parse XML to JSON
    try {
      const fileInZip = zip.file(path);
      if (!fileInZip) {
        return handleScormError();
      }

      const content: string = await fileInZip.async('string');
      const result = await new Promise<any>((resolve, reject) => {
        parseString(content, (err, res) => {
          if (err) {
            reject(err);
          } else {
            resolve(res);
          }
        });
      });

      const resources = result.manifest.resources[0].resource
        .map((res: any) => res.$)
        .filter((res: any) => {
          const scormType = res['adlcp:scormType'] || res['adlcp:scormtype'];
          return (
            scormType &&
            (scormType.toLowerCase() === 'sco' ||
              scormType.toLowerCase() === 'asset')
          );
        });
      if (!resources.length) {
        console.error(
          "No resources with adlcp:scorm[tT]ype of 'sco' or 'asset', file not supported"
        );
        return handleScormError();
      }

      return {
        autoSelectHtml: true,
        scormManifest: result.manifest,
        htmlFile: resources[0].href,
        latestUploadFile: latestFile,
        manifestUrl: path,
        linkType: 'html',
        uploading: true,
        file,
        title: file.name
      };
    } catch (err) {
      console.error(err);
      return handleScormError();
    }
  };

  const validateZipContent = async (file: FileType) => {
    // decompress zip file
    let errorStatus: string | null = null;
    const allFiles: string[] = [];
    const htmlFiles: string[] = [];
    const zipMimeTypes = [
      'application/zip',
      'application/zip-compressed',
      'application/x-zip-compressed',
      'multipart/x-zip'
    ];
    if (zipMimeTypes.includes(file.type)) {
      try {
        const zip = await JSZip.loadAsync(file.data);
        let latestDate = new Date(0);
        let latestFilePath = '';
        let imsManifestPath = '';
        let isScorm = false;

        zip.forEach((path, entry) => {
          allFiles.push(entry.name);
          if (entry.date > latestDate) {
            latestDate = entry.date;
            latestFilePath = path;
          }
          if (entry.name.indexOf('.html') !== -1) {
            htmlFiles.push(entry.name);
          }
          if (entry.name === 'imsmanifest.xml') {
            isScorm = true;
            imsManifestPath = path;
          } else if (/\/imsmanifest\.xml$/.test(entry.name)) {
            errorStatus = 'imsmanifest_not_in_root';
          }
        });

        if (isScorm) {
          return await handleScormParseImsManifest(
            file,
            zip,
            imsManifestPath,
            latestFilePath
          );
        } else {
          if (allFiles.length === 1 && /\.zip$/.test(allFiles[0])) {
            return handleScormError(
              messages.editorBlockScormNoManifestNestedZip
            );
          } else if (errorStatus === 'imsmanifest_not_in_root') {
            return handleScormError(messages.editorBlockScormManifestNotInRoot);
          }
          if (htmlFiles.length) {
            const fileError = getMessageWithPlaceholders({
              message: messages.editorBlockScormNoManifestUploadHtml,
              placeholders: { htmlFilesCount: htmlFiles.length }
            });
            return {
              file,
              fileError,
              htmlFile: htmlFiles[0],
              htmlFiles,
              autoSelectHtml: false,
              linkType: 'html',
              title: file.name
            };
          }
          return handleScormError(
            getMessageWithPlaceholders({
              message: messages.editorBlockScormNoManifestError,
              placeholders: { allFilesCount: allFiles.length }
            })
          );
        }
      } catch (err) {
        console.error(err);
        return handleScormError();
      }
    }
    return handleScormError();
  };

  const setScormUploadRefresh = async (fileUrls: FileUrls) => {
    try {
      await axios.get(fileUrls.fileUrl, { responseType: 'blob' });
      await axios.get(fileUrls.latestUploadUrl, { responseType: 'blob' });
      await axios.get(fileUrls.manifestUploadUrl, { responseType: 'blob' });
      setLoading(false);
    } catch (error) {
      if (errorCount > MAX_UPLOAD_FILE_POLLING_TRIES) {
        setLoading(false);
      } else {
        setErrorCount((prev) => prev + 1);
        scormUploadRefreshTimeout.current = window.setTimeout(() => {
          setScormUploadRefresh(fileUrls);
        }, 1000);
      }
    }
  };

  const handleUploadSuccess = async (values: {
    htmlFile: string;
    scormManifest: any;
    latestUploadFile: string;
    manifestUrl: string;
    title: string;
    url: string;
  }) => {
    let scormContents, scormObjectList;
    if (values.scormManifest) {
      scormContents = mapScormResources(values.scormManifest);
      scormObjectList = mapScormObjectList(scormContents);
    }

    let htmlUrl = values.htmlFile;
    if (scormObjectList && scormObjectList.length && scormObjectList[0].url) {
      htmlUrl = scormObjectList[0].url;
    }
    if (!htmlUrl) {
      console.error('No html resource found!');
      return;
    }

    const baseUrl = `${values.url.slice(
      0,
      values.url.lastIndexOf('/') + 1
    )}`.replace('/compressed/', '/decompressed/');

    const fileUrl = `${baseUrl}${htmlUrl}`;

    const latestUploadUrl = `${baseUrl}${values.latestUploadFile}`;

    const manifestUploadUrl = `${baseUrl}${values.manifestUrl}`;

    const scormData = {
      enableFullScreen: '{{ locales.enableFullScreen }}',
      file: '{{ locales.file }}',
      scormContents: '{{ locales.scormContents }}',
      scormManifest: '{{ locales.scormManifest }}',
      scormObjectList: '{{ locales.scormObjectList }}',
      locales: {
        ...data.locales,
        [languageVersion]: {
          ...data.locales[languageVersion],
          enableFullScreen: 'enabled',
          file: {
            title: values.title,
            url: fileUrl,
            compressedUrl: values.url,
            htmlUrl,
            baseUrl
          },
          scormContents,
          scormManifest: values.scormManifest,
          scormObjectList
        }
      }
    } satisfies Partial<BlockScorm>;

    const newData = { ...data, ...scormData };
    updateData(newData);

    // show loading until file is available
    setShowLanguageSpecificUploader(false);
    setLoading(true);
    scormUploadRefreshTimeout.current = window.setTimeout(() => {
      setScormUploadRefresh({
        fileUrl,
        latestUploadUrl,
        manifestUploadUrl
      });
    }, 500);
  };

  const handleReplace = () => {
    setShowLanguageSpecificUploader(true);
  };

  const handleReplaceCancel = () => {
    setShowLanguageSpecificUploader(false);
  };

  const handleRemove = () => {
    updateData({
      ...data,
      locales: {
        ...data.locales,
        [languageVersion]: {
          ...defaultBlockData.scorm.locales[languageVersion]
        }
      }
    });
  };

  const {
    activeLanguage,
    shouldShowDefaultFile,
    fileUrl: scormUrl
  } = getBlockFileForLanguage({
    data,
    courseLanguage: course.language,
    editorActiveLanguageVersion: editorLanguageActive,
    showLanguageSpecificUploader
  });

  const pointsWeight =
    data && 'pointsWeight' in data
      ? data.pointsWeight
      : defaultBlockData.scorm.pointsWeight;

  const isTask = data && 'isTask' in data ? data.isTask : true;

  const isEditingLanguageVersion = Boolean(editorLanguageActive);

  return (
    <div className="embed">
      {!scormUrl || showLanguageSpecificUploader ? (
        <div onClick={resetError}>
          <FileUploader
            allowedFileTypes={['.zip']}
            askForTitle={true}
            askToDecompressZip={true}
            disabled={isPreview}
            dragDrop={!isPreview}
            validateZipContent={validateZipContent}
            icon="file"
            id={`file-uploader-${blockId}`}
            keyPrefix="uploads/compressed/"
            onUploadSuccess={handleUploadSuccess}
            placeholder={messages.editorBlockEmbedChooseScormFile}
          />
        </div>
      ) : (
        <div className="inner">
          <TaskPointWeightSelector
            blockId={blockId}
            blockName={messages.blocksScorm}
            disabled={isPreview || isEditingLanguageVersion}
            handleTaskClick={handleIsTask}
            isEmbed={true}
            isMandatory={isTask && data?.isMandatory}
            isTask={isTask}
            pointsWeight={pointsWeight}
            onSelection={changePointsWeight}
          />

          <EditorBlockEmbedPreview
            blockId={blockId}
            blockType="scorm"
            data={data}
            dataFromDefaultLanguage={shouldShowDefaultFile}
            disableIsMandatorySettings={isEditingLanguageVersion}
            hideNonTranslatableSettings={shouldShowDefaultFile}
            isPreview={isPreview}
            languageVersion={activeLanguage}
            loading={loading}
            style={{ marginTop: 10 }}
            updateData={updateData}
            {...(!editorLanguageActive
              ? {
                  customControls: (
                    <EditorBlockEmbedFileControls
                      file={{
                        title: data.locales[activeLanguage]?.file?.title,
                        url: data.locales[activeLanguage]?.file?.compressedUrl
                      }}
                      type="scorm"
                    />
                  )
                }
              : {})}
            {...stateProps}
          />
        </div>
      )}

      {editorLanguageActive ? (
        <EditorBlockTranslationReplace
          customStatusText={messages.blocksFileUsingLanguageSpecific}
          defaultStatusText={messages.blocksFileUsingDefaultLanguage}
          downloadableFileUrl={
            data.locales[activeLanguage]?.file?.compressedUrl
          }
          hasCustomValue={!shouldShowDefaultFile}
          icon={['far', 'file']}
          onCancel={handleReplaceCancel}
          onRemove={handleRemove}
          onReplace={handleReplace}
          showCancel={showLanguageSpecificUploader}
        />
      ) : null}

      <style jsx>{`
        .embed {
          background-color: ${theme.lightGrey};
          border-radius: ${theme.cardBorderRadius};
        }
        .inner {
          padding: 15px;
        }
      `}</style>
    </div>
  );
};

export default EditorBlockScorm;
